import React from 'react';

/**
 * Compact, tasteful SVG national flags (viewBox 24x16) used as elegant
 * heraldic accents for cultural / independence seasonal themes.
 * Simplified but recognizable; emblems rendered as subtle marks.
 */
const R = ({ x, y, w, h, fill }) => <rect x={x} y={y} width={w} height={h} fill={fill} />;

const FLAGS = {
  // Guatemala — vertical light blue / white / light blue
  GT: () => (<><R x={0} y={0} w={24} h={16} fill="#fff" /><R x={0} y={0} w={8} h={16} fill="#4997d0" /><R x={16} y={0} w={8} h={16} fill="#4997d0" /></>),
  // El Salvador — horizontal blue / white / blue
  SV: () => (<><R x={0} y={0} w={24} h={16} fill="#fff" /><R x={0} y={0} w={24} h={5.33} fill="#0f47af" /><R x={0} y={10.66} w={24} h={5.34} fill="#0f47af" /></>),
  // Honduras — horizontal blue / white / blue
  HN: () => (<><R x={0} y={0} w={24} h={16} fill="#fff" /><R x={0} y={0} w={24} h={5.33} fill="#0073cf" /><R x={0} y={10.66} w={24} h={5.34} fill="#0073cf" /></>),
  // Nicaragua — horizontal blue / white / blue
  NI: () => (<><R x={0} y={0} w={24} h={16} fill="#fff" /><R x={0} y={0} w={24} h={5.33} fill="#0067c6" /><R x={0} y={10.66} w={24} h={5.34} fill="#0067c6" /></>),
  // Costa Rica — blue/white/red(wide)/white/blue
  CR: () => (<><R x={0} y={0} w={24} h={16} fill="#002b7f" /><R x={0} y={2.4} w={24} h={2.4} fill="#fff" /><R x={0} y={4.8} w={24} h={6.4} fill="#ce1126" /><R x={0} y={11.2} w={24} h={2.4} fill="#fff" /></>),
  // Mexico — vertical green/white/red + center mark
  MX: () => (<><R x={0} y={0} w={8} h={16} fill="#006847" /><R x={8} y={0} w={8} h={16} fill="#fff" /><R x={16} y={0} w={8} h={16} fill="#ce1126" /><circle cx={12} cy={8} r={1.6} fill="#8a5a2b" /></>),
  // Colombia — yellow(1/2) blue(1/4) red(1/4)
  CO: () => (<><R x={0} y={0} w={24} h={8} fill="#fcd116" /><R x={0} y={8} w={24} h={4} fill="#003893" /><R x={0} y={12} w={24} h={4} fill="#ce1126" /></>),
  // Argentina — light blue / white / light blue + sun
  AR: () => (<><R x={0} y={0} w={24} h={16} fill="#fff" /><R x={0} y={0} w={24} h={5.33} fill="#74acdf" /><R x={0} y={10.66} w={24} h={5.34} fill="#74acdf" /><circle cx={12} cy={8} r={1.7} fill="#f6b40e" /></>),
  // Chile — white/red, blue canton with star
  CL: () => (<><R x={0} y={0} w={24} h={8} fill="#fff" /><R x={0} y={8} w={24} h={8} fill="#d52b1e" /><R x={0} y={0} w={8} h={8} fill="#0039a6" /><polygon points="4,2.2 4.8,4.4 7.1,4.4 5.2,5.8 6,8 4,6.6 2,8 2.8,5.8 0.9,4.4 3.2,4.4" fill="#fff" /></>),
  // Puerto Rico — 5 red/white stripes + blue triangle + star
  PR: () => (<><R x={0} y={0} w={24} h={16} fill="#ed0000" /><R x={0} y={3.2} w={24} h={3.2} fill="#fff" /><R x={0} y={9.6} w={24} h={3.2} fill="#fff" /><polygon points="0,0 10,8 0,16" fill="#0050f0" /><polygon points="3,5.6 3.7,7.4 5.6,7.4 4.1,8.6 4.7,10.4 3,9.3 1.3,10.4 1.9,8.6 0.4,7.4 2.3,7.4" fill="#fff" /></>),
  // Dominican Republic — quartered blue/red with white cross
  DR: () => (<><R x={0} y={0} w={11} h={7} fill="#002d62" /><R x={13} y={0} w={11} h={7} fill="#ce1126" /><R x={0} y={9} w={11} h={7} fill="#ce1126" /><R x={13} y={9} w={11} h={7} fill="#002d62" /><R x={0} y={7} w={24} h={2} fill="#fff" /><R x={11} y={0} w={2} h={16} fill="#fff" /></>),
  // Spain — red/yellow(double)/red
  ES: () => (<><R x={0} y={0} w={24} h={16} fill="#aa151b" /><R x={0} y={4} w={24} h={8} fill="#f1bf00" /></>),
  // Peru — vertical red/white/red
  PE: () => (<><R x={0} y={0} w={24} h={16} fill="#fff" /><R x={0} y={0} w={8} h={16} fill="#d91023" /><R x={16} y={0} w={8} h={16} fill="#d91023" /></>),
  // Venezuela — yellow/blue/red
  VE: () => (<><R x={0} y={0} w={24} h={5.33} fill="#ffcc00" /><R x={0} y={5.33} w={24} h={5.33} fill="#00247d" /><R x={0} y={10.66} w={24} h={5.34} fill="#cf142b" /></>),
  // Ecuador — yellow(1/2)/blue/red
  EC: () => (<><R x={0} y={0} w={24} h={8} fill="#ffdd00" /><R x={0} y={8} w={24} h={4} fill="#034ea2" /><R x={0} y={12} w={24} h={4} fill="#ed1c24" /></>),
};

export function Flag({ code, className = '', style }) {
  const Draw = FLAGS[code];
  if (!Draw) return null;
  return (
    <svg viewBox="0 0 24 16" className={className} style={style} preserveAspectRatio="xMidYMid slice"
      role="img" aria-label={code}>
      <defs><clipPath id={`fclip-${code}`}><rect x="0" y="0" width="24" height="16" rx="2.5" /></clipPath></defs>
      <g clipPath={`url(#fclip-${code})`}>
        <Draw />
        <rect x="0.4" y="0.4" width="23.2" height="15.2" rx="2.3" fill="none" stroke="rgba(0,0,0,0.12)" strokeWidth="0.8" />
      </g>
    </svg>
  );
}

export const SUPPORTED_FLAGS = Object.keys(FLAGS);
